import { Component } from '@theme/component';
import { formatMoney } from '@theme/ilm-cart';

/**
 * Header search bar with live suggestions (Shopify predictive search).
 *
 * The markup is a plain GET form to /search, so searching works before this module loads.
 * This component adds an ARIA combobox: suggested searches, matching authors / publishers /
 * topics (collections) and books with cover and price, navigable with the arrow keys.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} input - The search field
 * @property {HTMLElement} panel - Suggestions popup
 * @property {HTMLElement} list - The listbox inside the panel
 *
 * @extends {Component<Refs>}
 */
class IlmSearch extends Component {
  requiredRefs = ['input', 'panel', 'list'];

  /** @type {AbortController | null} */
  #request = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #debounce;
  #active = -1;
  #lastQuery = '';

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('focusout', this.#onFocusOut);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('focusout', this.#onFocusOut);
    clearTimeout(this.#debounce);
    this.#request?.abort();
  }

  /** Debounced suggestions as the visitor types. */
  onInput() {
    clearTimeout(this.#debounce);
    this.#debounce = setTimeout(() => this.#suggest(this.refs.input.value.trim()), 160);
  }

  onFocus() {
    if (this.refs.list.childElementCount && this.refs.input.value.trim()) this.#open();
  }

  /** @param {KeyboardEvent} event */
  onKeydown(event) {
    const options = this.#options();
    switch (event.key) {
      case 'ArrowDown':
        if (!options.length) return;
        event.preventDefault();
        this.#open();
        this.#setActive(Math.min(options.length - 1, this.#active + 1));
        break;
      case 'ArrowUp':
        if (!options.length) return;
        event.preventDefault();
        this.#setActive(Math.max(-1, this.#active - 1));
        break;
      case 'Enter': {
        const option = options[this.#active];
        if (option instanceof HTMLAnchorElement) {
          event.preventDefault();
          window.location.href = option.href;
        }
        break;
      }
      case 'Escape':
        if (!this.refs.panel.hidden) {
          event.preventDefault();
          this.#close();
        }
        break;
    }
  }

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#close();
  };

  /** @param {string} query */
  async #suggest(query) {
    if (query === this.#lastQuery) return;
    this.#lastQuery = query;
    this.#request?.abort();
    if (query.length < 2) {
      this.refs.list.innerHTML = '';
      this.#close();
      return;
    }

    const controller = new AbortController();
    this.#request = controller;
    // eslint-disable-next-line no-undef
    const routes = typeof Theme !== 'undefined' ? Theme.routes : null;
    const url = new URL(`${routes?.predictive_search_url || '/search/suggest'}.json`, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'query,collection,product');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');

    try {
      const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const data = await response.json();
      if (controller.signal.aborted) return;
      this.#render(query, data?.resources?.results || {});
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
    }
  }

  /**
   * @param {string} query
   * @param {{ queries?: any[], collections?: any[], products?: any[] }} results
   */
  #render(query, results) {
    const labels = this.dataset;
    const queries = (results.queries || []).slice(0, 4);
    const collections = (results.collections || []).slice(0, 4);
    const products = (results.products || []).slice(0, 6);
    let index = 0;
    const id = () => `${this.refs.list.id}-opt-${index++}`;

    let html = '';
    if (queries.length) {
      html += `<li role="presentation" class="ilm-search__group">${escapeHtml(labels.labelSuggestions || '')}</li>`;
      for (const item of queries) {
        html += `<li role="presentation"><a role="option" id="${id()}" class="ilm-search__option ilm-search__option--query" href="${escapeAttr(item.url)}" tabindex="-1">${icon('search')}<span>${escapeHtml(item.text)}</span></a></li>`;
      }
    }
    if (collections.length) {
      html += `<li role="presentation" class="ilm-search__group">${escapeHtml(labels.labelCollections || '')}</li>`;
      for (const item of collections) {
        const image = item.featured_image?.url
          ? `<img src="${escapeAttr(sized(item.featured_image.url, 80))}" alt="" width="36" height="36" loading="lazy">`
          : `<span class="ilm-search__thumb-fallback">${escapeHtml((item.title || '?').charAt(0))}</span>`;
        html += `<li role="presentation"><a role="option" id="${id()}" class="ilm-search__option ilm-search__option--collection" href="${escapeAttr(item.url)}" tabindex="-1">${image}<span>${escapeHtml(item.title)}</span></a></li>`;
      }
    }
    if (products.length) {
      html += `<li role="presentation" class="ilm-search__group">${escapeHtml(labels.labelProducts || '')}</li>`;
      for (const item of products) {
        const price = Math.round(parseFloat(item.price || '0') * 100);
        const image = item.featured_image?.url || item.image;
        html += `<li role="presentation"><a role="option" id="${id()}" class="ilm-search__option ilm-search__option--product" href="${escapeAttr(item.url)}" tabindex="-1">
          ${image ? `<img src="${escapeAttr(sized(image, 120))}" alt="" width="48" height="48" loading="lazy">` : '<span class="ilm-search__thumb-fallback"></span>'}
          <span class="ilm-search__product"><span class="ilm-search__product-title" dir="auto">${escapeHtml(item.title)}</span>
          <span class="ilm-search__product-meta">${item.vendor ? `${escapeHtml(item.vendor)} · ` : ''}${price ? formatMoney(price) : ''}${item.available === false ? ` · ${escapeHtml(labels.labelSoldOut || '')}` : ''}</span></span>
        </a></li>`;
      }
    }

    const all = new URL(this.querySelector('form')?.getAttribute('action') || '/search', window.location.origin);
    all.searchParams.set('q', query);
    all.searchParams.set('options[prefix]', 'last');
    if (!queries.length && !collections.length && !products.length) {
      html += `<li role="presentation" class="ilm-search__empty">${escapeHtml((labels.labelEmpty || '').replace('[q]', query))}</li>`;
    }
    html += `<li role="presentation"><a role="option" id="${id()}" class="ilm-search__option ilm-search__all" href="${escapeAttr(all.toString())}" tabindex="-1">${escapeHtml((labels.labelAll || '').replace('[q]', query))}</a></li>`;

    this.refs.list.innerHTML = html;
    this.#active = -1;
    this.refs.input.removeAttribute('aria-activedescendant');
    if (document.activeElement === this.refs.input) this.#open();
  }

  #options() {
    return /** @type {HTMLElement[]} */ ([...this.refs.list.querySelectorAll('[role="option"]')]);
  }

  /** @param {number} index */
  #setActive(index) {
    const options = this.#options();
    options.forEach((option, i) => option.setAttribute('aria-selected', String(i === index)));
    this.#active = index;
    const active = options[index];
    if (active) {
      this.refs.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    } else {
      this.refs.input.removeAttribute('aria-activedescendant');
    }
  }

  #open() {
    this.refs.panel.hidden = false;
    this.refs.input.setAttribute('aria-expanded', 'true');
    this.classList.add('is-open');
  }

  #close() {
    this.refs.panel.hidden = true;
    this.refs.input.setAttribute('aria-expanded', 'false');
    this.refs.input.removeAttribute('aria-activedescendant');
    this.classList.remove('is-open');
    this.#active = -1;
  }
}

/** @param {string} url @param {number} width */
function sized(url, width) {
  try {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('width', String(width));
    return u.toString();
  } catch {
    return url;
  }
}

/** @param {string} name */
function icon(name) {
  return name === 'search'
    ? '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" stroke-width="2"/><path d="M16 16l4.5 4.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>'
    : '';
}

/** @param {unknown} value */
function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

/** @param {unknown} value */
function escapeAttr(value) {
  return escapeHtml(value);
}

if (!customElements.get('ilm-search')) customElements.define('ilm-search', IlmSearch);
